import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "../lib/api";
import { useSite } from "../lib/store";
import { CatIcon, ICONS } from "../components/Daisy";
import { Field, ImageInput, PageTitle, Toggle, useList } from "./ui";
import { Vouchers } from "./Vouchers";

const useCrud = (url) => {
  const [list, load, setList] = useList(url);
  const { reload } = useSite();
  const patch = (id, k, v) => setList(list.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const save = async (item) => {
    try {
      await adminApi.put(`${url}/${item.id}`, item);
      toast.success("Uloženo");
      reload();
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const create = async (body) => {
    await adminApi.post(url, body);
    load();
    reload();
  };
  const remove = async (item) => {
    if (!window.confirm("Opravdu smazat?")) return;
    await adminApi.delete(`${url}/${item.id}`);
    load();
    reload();
  };
  return { list, patch, save, create, remove };
};

const RowActions = ({ onSave, onDelete, testid }) => (
  <div className="flex gap-2">
    <button onClick={onSave} className="btn-sage !px-4 !py-2" data-testid={`${testid}-save`}><Save className="h-4 w-4" /> Uložit</button>
    <button onClick={onDelete} className="rounded-full p-2 text-[#D96B43] hover:bg-[#F8E1D7]" data-testid={`${testid}-delete`}><Trash2 className="h-4 w-4" /></button>
  </div>
);

export function Categories() {
  const { list, patch, save, create, remove } = useCrud("/categories");
  return (
    <div data-testid="admin-categories">
      <PageTitle title="Kategorie" sub="Název, fotka, ikona, pořadí a zobrazení na hlavní stránce" action={<button className="btn-sage" onClick={() => create({ name: "Nová kategorie", icon: "flower", order: (list?.length || 0) + 1, show_on_home: true })} data-testid="category-add-btn"><Plus className="h-4 w-4" /> Nová kategorie</button>} />
      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {(list || []).map((c, i) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="space-y-3 rounded-3xl border bg-white p-4" data-testid={`admin-category-${i}`}>
            <ImageInput value={c.image} onChange={(v) => patch(c.id, "image", v)} testid={`category-image-${i}`} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Název" value={c.name || ""} onChange={(e) => patch(c.id, "name", e.target.value)} testid={`category-name-${i}`} />
              <Field label="URL (slug)" value={c.slug || ""} onChange={(e) => patch(c.id, "slug", e.target.value)} testid={`category-slug-${i}`} />
            </div>
            <Field label="Popis" value={c.description || ""} onChange={(e) => patch(c.id, "description", e.target.value)} testid={`category-desc-${i}`} />
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#6E7565]">Ikona</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(ICONS).map((k) => (
                  <button key={k} type="button" onClick={() => patch(c.id, "icon", k)} className={`grid h-9 w-9 place-items-center rounded-full ${c.icon === k ? "bg-[#7A8460] text-white" : "bg-[#F7F3EA]"}`}><CatIcon name={k} className="h-4 w-4" /></button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <Field label="Pořadí" type="number" className="w-24" value={c.order ?? 0} onChange={(e) => patch(c.id, "order", Number(e.target.value))} testid={`category-order-${i}`} />
              <Toggle label="Na hlavní stránce" checked={!!c.show_on_home} onChange={(v) => patch(c.id, "show_on_home", v)} testid={`category-home-${i}`} />
            </div>
            <RowActions onSave={() => save(c)} onDelete={() => remove(c)} testid={`category-${i}`} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function HeroSlides() {
  const { list, patch, save, create, remove } = useCrud("/hero_slides");
  return (
    <div data-testid="admin-hero">
      <PageTitle title="Hero slider" sub="Obrázky a texty, které se střídají v úvodu hlavní stránky" action={<button className="btn-sage" onClick={() => create({ title: "Nový slide", subtitle: "", button_text: "Prozkoumat produkty", button_link: "/eshop", order: (list?.length || 0) + 1, active: true, badge: "" })} data-testid="hero-add-btn"><Plus className="h-4 w-4" /> Přidat slide</button>} />
      <div className="space-y-4">
        {(list || []).map((s, i) => (
          <div key={s.id} className="grid gap-4 rounded-3xl border bg-white p-4 md:grid-cols-[260px_1fr]" data-testid={`admin-hero-${i}`}>
            <ImageInput value={s.image} onChange={(v) => patch(s.id, "image", v)} className="h-56 w-full md:h-full" testid={`hero-image-${i}`} />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Nadpis" value={s.title || ""} onChange={(e) => patch(s.id, "title", e.target.value)} testid={`hero-title-${i}`} />
              <Field label="Odznak v kolečku" value={s.badge || ""} onChange={(e) => patch(s.id, "badge", e.target.value)} placeholder="např. PRO DĚTI 3–7 LET" testid={`hero-badge-${i}`} />
              <Field label="Podnadpis" className="sm:col-span-2" value={s.subtitle || ""} onChange={(e) => patch(s.id, "subtitle", e.target.value)} testid={`hero-subtitle-${i}`} />
              <Field label="Ručně psaný text" className="sm:col-span-2" value={s.tagline || ""} onChange={(e) => patch(s.id, "tagline", e.target.value)} testid={`hero-tagline-${i}`} />
              <Field label="Text tlačítka" value={s.button_text || ""} onChange={(e) => patch(s.id, "button_text", e.target.value)} testid={`hero-btn-text-${i}`} />
              <Field label="Odkaz tlačítka" value={s.button_link || ""} onChange={(e) => patch(s.id, "button_link", e.target.value)} testid={`hero-btn-link-${i}`} />
              <div className="flex items-end gap-4">
                <Field label="Pořadí" type="number" className="w-24" value={s.order ?? 0} onChange={(e) => patch(s.id, "order", Number(e.target.value))} testid={`hero-order-${i}`} />
                <Toggle label="Aktivní" checked={!!s.active} onChange={(v) => patch(s.id, "active", v)} testid={`hero-active-${i}`} />
              </div>
              <div className="flex items-end justify-end"><RowActions onSave={() => save(s)} onDelete={() => remove(s)} testid={`hero-${i}`} /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Coupons() {
  const { list, patch, save, create, remove } = useCrud("/coupons");
  const [code, setCode] = useState("");
  return (
    <div data-testid="admin-coupons">
      <PageTitle title="Slevové kódy" sub="Procentní nebo pevné slevy, limity použití a platnost" />
      <form onSubmit={(e) => { e.preventDefault(); if (code) create({ code, type: "percent", value: 10, active: true, max_uses: null, valid_until: "", min_total: 0 }).then(() => setCode("")); }} className="mb-6 flex max-w-md gap-2">
        <input className="input-soft uppercase" placeholder="KÓD, např. JARO10" value={code} onChange={(e) => setCode(e.target.value)} data-testid="coupon-code-input" />
        <button className="btn-sage" data-testid="coupon-add-btn"><Plus className="h-4 w-4" /> Přidat</button>
      </form>
      <div className="space-y-3">
        {(list || []).map((c, i) => (
          <div key={c.id} className="flex flex-wrap items-end gap-3 rounded-3xl border bg-white p-4" data-testid={`admin-coupon-${i}`}>
            <Field label="Kód" className="w-36" value={c.code} onChange={(e) => patch(c.id, "code", e.target.value)} testid={`coupon-code-${i}`} />
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#6E7565]">Typ</span>
              <select className="input-soft" value={c.type} onChange={(e) => patch(c.id, "type", e.target.value)} data-testid={`coupon-type-${i}`}><option value="percent">Procenta %</option><option value="fixed">Částka Kč</option></select></label>
            <Field label="Hodnota" type="number" className="w-28" value={c.value} onChange={(e) => patch(c.id, "value", Number(e.target.value))} testid={`coupon-value-${i}`} />
            <Field label="Min. objednávka" type="number" className="w-32" value={c.min_total || 0} onChange={(e) => patch(c.id, "min_total", Number(e.target.value))} testid={`coupon-min-${i}`} />
            <Field label="Max. použití" type="number" className="w-28" value={c.max_uses ?? ""} onChange={(e) => patch(c.id, "max_uses", e.target.value ? Number(e.target.value) : null)} testid={`coupon-max-${i}`} />
            <Field label="Platí do" type="date" className="w-40" value={c.valid_until || ""} onChange={(e) => patch(c.id, "valid_until", e.target.value)} testid={`coupon-until-${i}`} />
            <p className="pb-3 text-sm text-[#6E7565]">Použito: <b>{c.used || 0}×</b></p>
            <div className="pb-2"><Toggle label="Aktivní" checked={!!c.active} onChange={(v) => patch(c.id, "active", v)} testid={`coupon-active-${i}`} /></div>
            <div className="ml-auto"><RowActions onSave={() => save(c)} onDelete={() => remove(c)} testid={`coupon-${i}`} /></div>
          </div>
        ))}
      </div>
      <Vouchers />
    </div>
  );
}
